package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.MarqueeContainerStyle
import com.iregained.site.components.sections.home.styles.MarqueeTrackStyle
import com.iregained.site.components.sections.home.styles.PartnerLogoStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Composable
fun HomePartnerLogosMarqueeSection(data: HomePartnerLogosMarqueeSectionData = defaultHomePartnerLogosMarqueeSectionData) {
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-partner-logos-marquee"
    val palette = ColorMode.current.toSitePalette()

    var isVisible by remember { mutableStateOf(false) }

    val safeLogosList = if (data.logos.isNotEmpty() && data.logos.size <= 5) {
        data.logos + data.logos + data.logos
    } else {
        data.logos
    }

    if (data.logos.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element)
                            onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    // UNIQUE ANIMATION: A slow 1.5 second pure opacity fade
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px).opacity(if (isVisible) 1 else 0)
                        .transition(Transition.of("opacity", 1.5.s))
                ) {

                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).padding(leftRight = 5.percent),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    Box(
                        modifier = MarqueeContainerStyle.toModifier().fillMaxWidth().padding(leftRight = 50.px)
                            .overflow(Overflow.Hidden).display(DisplayStyle.Flex).styleModifier {
                                property(
                                    "-webkit-mask-image",
                                    "linear-gradient(to right, transparent, black 10%, black 90%, transparent)"
                                )
                            }) {
                        MarqueeTrack(safeLogosList)
                        MarqueeTrack(safeLogosList)
                    }
                }
            }
        }
    }
}

@Composable
private fun MarqueeTrack(logos: List<PartnerLogoData>) {
    Row(
        modifier = MarqueeTrackStyle.toModifier().display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(80.px)
            .padding(right = 80.px)
    ) {
        logos.forEachIndexed { index, data ->
            key("${data.altText}_$index") {
                PartnerLogo(data)
            }
        }
    }
}

@Composable
private fun PartnerLogo(data: PartnerLogoData) {
    if (data.imageUrl.isNotBlank()) Image(
        src = data.imageUrl,
        description = data.altText,
        modifier = PartnerLogoStyle.toModifier().unextractable().maxWidth(250.px).height(100.px)
            .objectFit(ObjectFit.Contain)
    )
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field imageUrl:
 * - Aspect Ratio: Flexible, typically 3:1 or 2:1 (Horizontal Logo).
 * - Recommended Dimensions: Max width 400px.
 * - Format: SVG or transparent PNG ONLY. No white backgrounds.
 * - Max File Size: 100KB.
 * - Behavior: Enforced max-width 250px, height 100px. ObjectFit.Contain prevents cropping and preserves logo integrity.
 */
data class PartnerLogoData(val imageUrl: String, val altText: String)

data class HomePartnerLogosMarqueeSectionData(
    val sectionId: String? = null, val sectionHeader: String, val logos: List<PartnerLogoData>
)

val defaultHomePartnerLogosMarqueeSectionData = HomePartnerLogosMarqueeSectionData(
    sectionId = "home-partner-logos-marquee", sectionHeader = "OUR LEADING PARTNERS AND CUSTOMERS", logos = listOf(
        PartnerLogoData(Res.Images.Home.TrustLogos.HSN, "Health Sciences North"),
        PartnerLogoData(Res.Images.Home.TrustLogos.LU, "Laurentian University"),
        PartnerLogoData(Res.Images.Home.TrustLogos.MAYO_ASU, "Mayo Clinic and ASU"),
        PartnerLogoData(Res.Images.Home.TrustLogos.BAYCREST, "Baycrest"),
        PartnerLogoData(Res.Images.Home.TrustLogos.RVH_HOSPITAL, "Royal Victoria Regional Health Centre"),
        PartnerLogoData(Res.Images.Home.TrustLogos.UNIVERSITY_OF_TEXAS, "University of Texas at Dallas"),
        PartnerLogoData(Res.Images.Home.TrustLogos.UNIVERSITY_OF_CINCINNATI, "University of Cincinnati"),
        PartnerLogoData(Res.Images.Home.TrustLogos.UT_HEALTH_SAN_ANTONIO, "UT Health San Antonio"),
        PartnerLogoData(Res.Images.Home.TrustLogos.UH_St_JOHN_HOSPITAL, "UH St John Medical Center"),
        PartnerLogoData(Res.Images.Home.TrustLogos.CMC_VELLORE, "Christian Medical College Vellore"),
        PartnerLogoData(Res.Images.Home.TrustLogos.TAIPEI_UNIVERSITY, "Taipei Medical University"),
        PartnerLogoData(
            Res.Images.Home.TrustLogos.BIOMED_ACC_TAIPEI_UNIVERSITY, "Biomedical Accelerator Taipei Medical University"
        ),
        PartnerLogoData(
            Res.Images.Home.TrustLogos.PROTOCARE, "ProtoCare"
        )
    )
)